import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus,
    faTimesCircle,
    faBuilding,
    faPlusCircle,
    faMinusCircle,
    faGlobe,
    faUserGroup,
    faCheckCircle,
    faCopy
} from '@fortawesome/free-solid-svg-icons';
import RoleCheckboxList from './RoleCheckboxList';
import './edit-user-modal.scss';

const entityTypeArr = { 2: 'Network', 3: 'Chain', 4: 'Club' };

const findCorrectKey = (e) => {
    if (e?.EntityType === 2) return e.EntityID;
    if (e?.EntityType === 3) return e.ChainID;
    return e.ClubID;
};

const findCorrectIcon = (e) => {
    if (e?.EntityType === 2) return faGlobe;
    if (e?.EntityType === 3) return faBuilding;
    return faUserGroup;
};

function TemporaryPasswordDisplay({ password }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(password)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2500);
            })
            .catch(() => {});
    };

    return (
        <div className="alert alert-secondary d-flex align-items-center justify-content-between mb-4 py-2 px-3">
            <span>
                <strong>Temporary password: </strong>
                <span className="font-monospace">{password}</span>
            </span>
            {copied
                ? <span className="ms-2 text-success small">Copied!</span>
                : <FontAwesomeIcon icon={faCopy} onClick={handleCopy} style={{ cursor: 'pointer' }} title="Copy password" />
            }
        </div>
    );
}

export default function CreateUserModal({
    show,
    onClose,

    email,
    onEmailChange,
    customerCode,
    onCustomerCodeChange,
    temporaryPassword,
    errors,

    updatedRoles,
    onRolesChange,

    entityTree,
    onRemoveEntity,

    isAddToggled,
    onToggleAddMode,

    isNetworksActive,
    isChainsActive,
    isClubsActive,
    handleBtnGroupChange,

    entityAddData,
    entitySearchLike,
    onEntitySearchLikeChange,
    entityIDSearch,
    onEntityIDSearchChange,

    countryIdFilter,
    onCountryIdFilterChange,

    onAddEntity,
    isEntityAdded,

    onSave,
    isValid,
    isSaving,

    isUKUser,
}) {
    return (
        <Modal className="dialog-container" show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <FontAwesomeIcon icon={faUserPlus} className="editing-user-icon" />
                <Modal.Title>Create User</Modal.Title>
            </Modal.Header>

            <Modal.Body className="dialog-body">
                {/* Email */}
                <label className="fw-bold">Email</label>
                <div className="form-outline mb-2">
                    <input
                        className={`form-control form-control-lg${errors?.email ? ' is-invalid' : ''}`}
                        onChange={(e) => onEmailChange(e.target.value)}
                        type="email"
                        value={email}
                        placeholder="Enter email address"
                    />
                    {errors?.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                {/* Customer Code */}
                <label className="fw-bold">Customer Code</label>
                <div className="form-outline mb-4">
                    <input
                        className={`form-control form-control-lg${errors?.customerCode ? ' is-invalid' : ''}`}
                        onChange={(e) => onCustomerCodeChange(e.target.value)}
                        type="text"
                        value={customerCode}
                        placeholder="Enter customer code"
                    />
                    {errors?.customerCode && <div className="invalid-feedback">{errors.customerCode}</div>}
                </div>

                {/* Temporary Password */}
                <TemporaryPasswordDisplay password={temporaryPassword} />

                {/* Roles */}
                <div className="mb-3">
                    <div className="fw-bold">Roles</div>
                    <RoleCheckboxList
                        initialRoles={updatedRoles}
                        onChange={(roles) => onRolesChange(roles)}
                    />
                </div>

                {/* Entities */}
                <div className="mb-3 eu-entity-tree-container">
                    <div className="networks-title">
                        <FontAwesomeIcon className="eu-entity-icon" icon={faGlobe} />
                        Add Venues

                        {isAddToggled ? (
                            <button
                                type="button"
                                className="add-entities-btn"
                                onClick={onToggleAddMode}
                                title="Add Entities"
                            >
                                Add
                                <FontAwesomeIcon className="add-entities-btn-icon" icon={faPlusCircle} />
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="add-entities-btn"
                                onClick={onToggleAddMode}
                                title="Return"
                            >
                                Return to List
                                <FontAwesomeIcon className="add-entities-btn-icon" icon={faMinusCircle} />
                            </button>
                        )}
                    </div>

                    {isAddToggled ? (
                        <ul className="eu-entity-tree">
                            {entityTree.length === 0 && (
                                <li className="eu-entity-row text-muted" style={{ fontSize: '0.9rem', padding: '6px 0' }}>
                                    No venues assigned yet
                                </li>
                            )}
                            {entityTree.map((entity) => {
                                const key = findCorrectKey(entity);
                                return (
                                    <li key={`${entity.EntityType}-${key}`} className="eu-entity-row">
                                        <div className="eu-entity-line">
                                            <span className="eu-entity-label">
                                                <FontAwesomeIcon className="eu-entity-icon" icon={findCorrectIcon(entity)} />
                                                {entityTypeArr[entity.EntityType]}: {key} - {entity.EntityName}
                                            </span>
                                            <button
                                                type="button"
                                                className="icon-btn remove"
                                                onClick={() => onRemoveEntity(entity)}
                                                title="Remove"
                                            >
                                                <FontAwesomeIcon className="remove-icon" icon={faTimesCircle} />
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <div>
                            <div className="eu-btn-group">
                                {countryIdFilter === 2 && (
                                    <button type="button" className={isNetworksActive ? 'group-btn-active' : 'btn-group-btn'} onClick={() => handleBtnGroupChange('network')}>
                                        <FontAwesomeIcon className={isNetworksActive ? 'btn-group-icon-active' : 'btn-group-icon'} icon={faGlobe} />
                                        Networks
                                    </button>
                                )}
                                {countryIdFilter === 2 && (
                                    <button type="button" className={isChainsActive ? 'group-btn-active' : 'btn-group-btn'} onClick={() => handleBtnGroupChange('chain')}>
                                        <FontAwesomeIcon className={isChainsActive ? 'btn-group-icon-active' : 'btn-group-icon'} icon={faBuilding} />
                                        Chains
                                    </button>
                                )}
                                <button type="button" className={isClubsActive ? 'group-btn-active' : 'btn-group-btn'} onClick={() => handleBtnGroupChange('club')}>
                                    <FontAwesomeIcon className={isClubsActive ? 'btn-group-icon-active' : 'btn-group-icon'} icon={faUserGroup} />
                                    Clubs
                                </button>
                                <select
                                    className="btn-group-select"
                                    value={countryIdFilter}
                                    onChange={(e) => onCountryIdFilterChange?.(Number(e.target.value))}
                                    title="Filter by Country"
                                >
                                    <option value={1}>US</option>
                                    <option value={2}>UK</option>
                                </select>
                            </div>
                            <div className="entity-search-container">
                                <span className="form-outline mb-4 entity-search-name">
                                    <input
                                        className="form-control form-control-lg entity-search-name"
                                        onChange={(e) => onEntitySearchLikeChange(e.target.value)}
                                        type="text"
                                        value={entitySearchLike ?? ''}
                                        placeholder="Search by Name"
                                    />
                                </span>
                                <span className="form-outline mb-4 entity-searchid">
                                    <input
                                        className="form-control form-control-lg entity-search-id"
                                        onChange={(e) => onEntityIDSearchChange(e.target.value)}
                                        type="text"
                                        value={entityIDSearch ?? ''}
                                        placeholder="Find by ID"
                                    />
                                </span>
                            </div>
                            {entityAddData?.length >= 250 && <label>250+ record(s) found:</label>}
                            {entityAddData?.length < 250 && <label>{entityAddData?.length ?? 0} record(s) found:</label>}
                            <ul className="eu-entity-tree">
                                {entityAddData?.map((entity) => {
                                    const key = findCorrectKey(entity);
                                    const added = isEntityAdded(entity);
                                    return (
                                        <li key={`${entity.EntityType}-${key}`} className="eu-entity-row">
                                            <div className="eu-entity-line">
                                                <span className="eu-entity-label">
                                                    <FontAwesomeIcon className="eu-entity-icon" icon={findCorrectIcon(entity)} />
                                                    {entityTypeArr[entity.EntityType]}: {key} - {entity.EntityName}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="icon-btn remove"
                                                    onClick={() => (added ? onRemoveEntity(entity) : onAddEntity(entity))}
                                                    title={added ? 'Added' : 'Add'}
                                                >
                                                    <FontAwesomeIcon className="remove-icon" icon={added ? faCheckCircle : faPlusCircle} />
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={onSave} disabled={!isValid || isSaving}>
                    {isSaving ? 'Creating...' : 'Create'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
